import { useHomeCrumb } from "@/components/PageBreadcrumbs";
import {
  snackSupplierOptions,
  useBookedSlots,
  useBuilding,
  useCompanyContact,
  useMeetingRooms,
} from "@/api";
import { Accordion, Breadcrumbs, Icon } from "@app-components";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import styles from "@/styles/Bookings.module.scss";
import {
  DEFAULT_BOOKING_SETTINGS,
  formatHoursToMinutesAndHours,
  isSnackOrderDeliveryMethod,
  isSnackOrderPickupMethod,
  joinWithAnd,
  MEETING_ROOM_FEATURES,
} from "@/lib";
import Calendar from "@/components/bookings/calendar";
import { SlotInfo } from "react-big-calendar";
import BookingConfirmationModal from "@/components/bookings/BookingConfirmationModal";
import {
  dateToDateInputString,
  dateToTimeInputString,
  formatFloorNumber,
  formatOpenOnDays,
  formatRoomPrice,
  NOK,
} from "@/lib";
import MeetingRoomLoading from "@/components/bookings/MeetingRoomLoading";
import { useWelcomeBookingAccess } from "@/hooks/useWelcomeBookingAccess";
import PageBackButton from "@/components/PageBackButton";
import { MeetingRoomBookingPermission } from "@/types";
import MeetingRoomPlaceholder from "@/assets/meeting-room-placeholder.svg";
import { useSessionState } from "@/hooks/useSessionState";
import ImageCarousel from "@/components/ImageCarousel";
import useSnackOrderStates from "@/hooks/useSnackOrderStates";
import { useLoggedInUser } from "@/store/zustandStore";
import { useBuildingQuery } from "@/components/RouterSetup";
import { useWelcomeSnackAccess } from "@/hooks/useWelcomeSnackAccess";

export const Route = createFileRoute("/_portal/bookings/room/$roomId")({
  component: MeetingRoomDetails,
});

function MeetingRoomDetails() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useLoggedInUser();
  const { allowWelcomeBookingAccess } = useWelcomeBookingAccess();
  const { hasWelcomeSnack } = useWelcomeSnackAccess(true, true);
  const { data: building } = useBuilding();
  const { resetOrderStates } = useSnackOrderStates();
  const { data: companyContact } = useCompanyContact(user.buildingTenantId);
  const { data: supplier } = useBuildingQuery(
    snackSupplierOptions(building.supplierCompanyId),
  );
  const deliveryVariants = (supplier?.deliveryTypes ?? []).filter(
    isSnackOrderDeliveryMethod,
  );
  const pickupVariants = (supplier?.deliveryTypes ?? []).filter(
    isSnackOrderPickupMethod,
  );
  const deliveryEnabled = deliveryVariants.length > 0;
  const defaultDeliveryType = deliveryEnabled
    ? deliveryVariants[0]
    : pickupVariants[0];
  const { roomId } = Route.useParams();
  const {
    data: rooms,
    isLoading,
    isError,
  } = useMeetingRooms(allowWelcomeBookingAccess);
  const [tab] = useSessionState<string>("meetingRoomsTab", "all");
  const room = useMemo(
    () => rooms?.find((r) => r.roomId === roomId),
    [rooms, roomId],
  );
  const openingHours = useMemo(
    () => ({
      opensAt: room?.opensAt || DEFAULT_BOOKING_SETTINGS.minTime,
      closesAt: room?.closesAt || DEFAULT_BOOKING_SETTINGS.maxTime,
      closedOnDays: room?.closedOnDays || [],
    }),
    [room],
  );
  const images = useMemo(
    () => [room?.thumbnail ?? "", room?.images ?? []].flat().filter(Boolean),
    [room],
  );
  const userBookingPermissionLevel: MeetingRoomBookingPermission = useMemo(
    () =>
      building.buildingAdmins.includes(user.userId)
        ? MeetingRoomBookingPermission.BUILDING_ADMIN
        : user.adminInCompanies.includes(user.buildingTenantId)
          ? MeetingRoomBookingPermission.COMPANY_ADMIN
          : MeetingRoomBookingPermission.ALL,
    [building, user],
  );
  const userCanBook = useMemo(
    () =>
      userBookingPermissionLevel ===
        MeetingRoomBookingPermission.BUILDING_ADMIN ||
      room?.allowBookingFor === MeetingRoomBookingPermission.ALL ||
      room?.allowBookingFor === userBookingPermissionLevel,
    [userBookingPermissionLevel, room],
  );
  const subRoomNames = useMemo(
    () =>
      rooms && room?.subRoomIds?.length
        ? room.subRoomIds
            .map((id) => rooms?.find((r) => r.roomId === id)?.roomName ?? "")
            .filter((n) => !!n)
        : [],
    [rooms, room],
  );
  const homeCrumb = useHomeCrumb();
  const breadcrumbs = [
    ...homeCrumb,
    { label: t("SIDEBAR.BOOKINGS"), path: "/bookings" },
    {
      label: room?.roomName || roomId,
      path: `/bookings/room/${roomId}`,
    },
  ];
  const [selectedSlot, setSelectedSlot] = useState<SlotInfo>();
  const { data: bookedSlots } = useBookedSlots(
    { roomId: room?.roomId || "" },
    allowWelcomeBookingAccess,
  );

  if (isError || (rooms && !room))
    throw new Error("Failed to load meeting rooms");

  if (!allowWelcomeBookingAccess) return null;

  return isLoading ? (
    <MeetingRoomLoading />
  ) : (
    <div className={`pageWithoutBottomSpacing ${styles.container}`}>
      <div className="backAndBreadcrumbsContainer">
        <PageBackButton fallbackPath={{ to: "/bookings", hash: tab }} />
        <Breadcrumbs
          levels={breadcrumbs}
          onNavigate={(path) => navigate({ to: path })}
        />
      </div>
      <h1>{room?.roomName}</h1>
      <div className={styles.roomInfoContainer}>
        <div className={styles.imageCarouselContainer}>
          {room?.thumbnail || room?.images?.length ? (
            <>
              {images.length > 1 ? (
                <ImageCarousel images={images} />
              ) : (
                <img src={images[0]} alt={room?.roomName} />
              )}
            </>
          ) : (
            <img src={MeetingRoomPlaceholder} alt="Placeholder" />
          )}
        </div>
        <div className={styles.textContainer}>
          {!!room?.subRoomIds?.length && (
            <div className="banner">
              <Icon name="link" />
              <p>
                {t("BOOKINGS.THIS_IS_A_COMBINATION")}{" "}
                {joinWithAnd(
                  subRoomNames.map((name) => <strong>{name}</strong>),
                  t("BADGE_PRINTING.PRINT.AND"),
                )}
              </p>
            </div>
          )}
          <div className={styles.quickInfo}>
            <p>
              <Icon name="location_on" /> {formatFloorNumber(room?.floor, t)}
              {room?.location && room.location[i18n.language as "en" | "no"]
                ? `, ${room.location[i18n.language as "en" | "no"]}`
                : ""}
            </p>
            <p>
              <Icon name="group" /> {room?.capacity}{" "}
              {t("PEOPLE", { count: room?.capacity }).toLowerCase()}
            </p>
            <p>
              <Icon name="schedule" /> {openingHours.opensAt} -{" "}
              {openingHours.closesAt}
              {`, ${formatOpenOnDays(openingHours.closedOnDays, i18n.language, t)}`}
            </p>
            {!!room?.bufferTimeInHours && (
              <p>
                <Icon name="hourglass_empty" />{" "}
                {t("BOOKINGS.BUFFER_TIME", {
                  buffer: formatHoursToMinutesAndHours(
                    room.bufferTimeInHours,
                    t,
                    true,
                  ),
                })}
              </p>
            )}
            {(!!room?.hourlyPrice || !!room?.fixedBookingFee) && (
              <p>
                <Icon name="money" />{" "}
                {formatRoomPrice({
                  fixedFee: room?.fixedBookingFee,
                  hourlyRate: room?.hourlyPrice,
                  t,
                })}
              </p>
            )}
            {!!room?.maxPricePerBooking && (
              <p>
                <Icon name="money_arrow_down" /> Max{" "}
                {NOK.format(room.maxPricePerBooking)} /{" "}
                {t("BOOKINGS.BOOKING").toLowerCase()}
              </p>
            )}
          </div>
          <div>
            {!!room?.description[i18n.language as "en" | "no"]?.length && (
              <>
                <h2 style={{ fontSize: "var(--wlcm-text-default)" }}>
                  {t("BOOKINGS.DESCRIPTION")}
                </h2>
                <Accordion
                  paragraph
                  id={`${room?.roomId}-description`}
                  body={room?.description[i18n.language as "en" | "no"] ?? ""}
                  expandText={t("EXPAND_PARAGRAPH")}
                  collapseText={t("COLLAPSE_PARAGRAPH")}
                />
              </>
            )}
            {(room?.meetingFoodUrl || hasWelcomeSnack) && (
              <p className="highlightedInfo">
                <Icon name="restaurant" />
                <span>
                  <Trans
                    i18nKey="BOOKINGS.MEETING_FOOD"
                    components={{
                      url: hasWelcomeSnack ? (
                        <Link
                          to="/orders/new"
                          search={{
                            deliveryType: defaultDeliveryType,
                            deliveryInstructions: room
                              ? `${room.roomName} - ${formatFloorNumber(room.floor, t)}${room.location ? `, ${room.location[i18n.language === "en" ? "en" : "no"]}` : ""}`
                              : undefined,
                          }}
                          onClick={() => resetOrderStates()}
                        ></Link>
                      ) : (
                        <a href={room?.meetingFoodUrl} target="_blank"></a>
                      ),
                      icon: (
                        <Icon
                          name={
                            hasWelcomeSnack ? "arrow_forward" : "open_in_new"
                          }
                          style={{ fontSize: "1em" }}
                        />
                      ),
                    }}
                  />
                </span>
              </p>
            )}
          </div>
          <div className={styles.largeFeaturesContainer}>
            <h2 className="sr-only">Features</h2>
            {room?.features.map((feature) => (
              <p className={styles.largeFeatureChip} key={feature}>
                <Icon name={MEETING_ROOM_FEATURES[feature]?.icon} />
                {t(`BOOKINGS.ROOM_FEATURES.${feature.toUpperCase()}`)}
              </p>
            ))}
          </div>
        </div>
      </div>
      <div className={styles.calendarContainer} id="availability">
        <h2>{t("BOOKINGS.AVAILABILITY.HEADER")}</h2>
        <p>
          <Trans
            i18nKey="BOOKINGS.AVAILABILITY.DESCRIPTION"
            components={{
              bold: (
                <strong
                  style={!userCanBook ? { display: "none" } : {}}
                ></strong>
              ),
            }}
          />
        </p>
        {!userCanBook && (
          <p className="banner warningBanner">
            <Icon name="info" />{" "}
            <div>
              <Trans
                i18nKey={`BOOKINGS.AVAILABILITY.BANNER.${room?.allowBookingFor.toUpperCase()}`}
                values={{
                  companyContact: companyContact
                    ? companyContact.name
                    : t("BOOKINGS.AVAILABILITY.BANNER.NO_COMPANY_CONTACT"),
                }}
                components={{
                  mailto: companyContact ? (
                    <a
                      href={`mailto:${companyContact.email}`}
                      style={{ color: "black" }}
                    ></a>
                  ) : (
                    <></>
                  ),
                }}
              />
            </div>
          </p>
        )}
        <Calendar
          events={bookedSlots || []}
          minTime={room?.opensAt}
          maxTime={room?.closesAt}
          maxDuration={building?.bookingSettings?.maxDuration}
          closedOnDays={room?.closedOnDays}
          blockedTimeSlots={room?.blockBookingOn}
          maxDaysAhead={building?.bookingSettings?.maxDaysAhead}
          onSlotSelected={userCanBook ? setSelectedSlot : undefined}
        />
      </div>
      {selectedSlot && room && userCanBook && (
        <BookingConfirmationModal
          open={selectedSlot && room && userCanBook}
          room={room}
          maxBookingsPerPerson={building?.bookingSettings?.maxBookingsPerPerson}
          maxDuration={building?.bookingSettings?.maxDuration}
          blockedTimeSlots={room?.blockBookingOn}
          date={dateToDateInputString(selectedSlot.start)}
          startTime={dateToTimeInputString(selectedSlot.start)}
          endTime={dateToTimeInputString(selectedSlot.end)}
          onClose={() => setSelectedSlot(undefined)}
          editableTimes
        />
      )}
    </div>
  );
}
